import is from '@sindresorhus/is';
import clsx from 'clsx';
import type {
    CSSProperties,
    FocusEvent,
    FormEvent,
    FunctionComponent,
    HTMLAttributes,
    KeyboardEvent,
    ReactNode,
    Ref,
} from 'react';
import { useCallback, useLayoutEffect, useRef } from 'react';
import type { UseFormRegisterReturn } from 'react-hook-form';

import { useComposedRefs } from '../../../utils/compose-refs';

const TITLE_TYPOGRAPHY = 'text-left text-text-primary text-[1.375rem] font-medium leading-normal';

const TITLE_STYLE: CSSProperties = { overflowWrap: 'anywhere' };
const TITLE_LINE_BREAK_PATTERN = /\s*[\r\n]+\s*/g;

const normalizeTitleValue = (value: string): string => value.replace(TITLE_LINE_BREAK_PATTERN, ' ');

const getEditableTitleValue = (element: HTMLElement): string => normalizeTitleValue(element.textContent ?? '');

type TitleProps = HTMLAttributes<HTMLDivElement> & {
    readonly children: ReactNode;
};

export const Title: FunctionComponent<TitleProps> = ({ children, className, style, ...rest }) => (
    <div className={clsx(TITLE_TYPOGRAPHY, className)} style={{ ...TITLE_STYLE, ...style }} {...rest}>
        {children}
    </div>
);

type SidepanelTitleProps = {
    readonly className?: string;
    readonly commentHighlightClassName?: string;
    readonly hasComment?: boolean;
    readonly inputRef?: Ref<HTMLSpanElement>;
    readonly isCommentActive?: boolean;
    readonly isEditing: boolean;
    readonly placeholder?: string;
    readonly title: string;
    readonly onInput?: () => void;
    readonly onRowClick?: () => void;
    readonly register?: UseFormRegisterReturn;
    readonly rowAttrs?: Record<string, string>;
    readonly stamps?: ReactNode;
};

const TITLE_INPUT_TYPOGRAPHY = 'text-left text-text-primary text-[1.375rem] font-medium leading-normal tracking-normal';

export const SidepanelTitle: FunctionComponent<SidepanelTitleProps> = ({
    className,
    commentHighlightClassName,
    hasComment = false,
    inputRef,
    isCommentActive = false,
    isEditing,
    onInput,
    onRowClick,
    placeholder,
    register,
    rowAttrs,
    stamps,
    title,
}) => {
    const latestTitleRef = useRef(title);
    const hasNotifiedInputRef = useRef(false);
    const editableTitleRef = useRef<HTMLSpanElement | null>(null);
    const hiddenInputElementRef = useRef<HTMLInputElement | null>(null);

    latestTitleRef.current = title;

    const setHiddenInputElement = useCallback((element: HTMLInputElement | null): void => {
        hiddenInputElementRef.current = element;
        if (!is.null(element)) {
            element.value = latestTitleRef.current;
        }
    }, []);

    const setEditableTitleElement = useCallback((element: HTMLSpanElement | null): void => {
        editableTitleRef.current = element;
        if (is.null(element)) return;
        if (element.textContent === latestTitleRef.current) return;
        element.textContent = latestTitleRef.current;
    }, []);

    const hiddenInputRef = useComposedRefs<HTMLInputElement>(setHiddenInputElement, register?.ref);
    const composedInputRef = useComposedRefs<HTMLSpanElement>(inputRef, setEditableTitleElement);

    useLayoutEffect(() => {
        hasNotifiedInputRef.current = false;
        if (!is.null(hiddenInputElementRef.current)) {
            hiddenInputElementRef.current.value = title;
        }
        const inputElement = editableTitleRef.current;
        if (is.null(inputElement)) return;
        if (document.activeElement === inputElement) return;
        if (inputElement.textContent === title) return;
        inputElement.textContent = title;
    }, [title]);

    const rowClasses = clsx('flex w-full min-w-0 flex-wrap items-center justify-between gap-m', className);
    const hasHighlight = !is.undefined(commentHighlightClassName) && commentHighlightClassName.trim().length > 0;
    const hasPlaceholder = is.nonEmptyStringAndNotWhitespace(placeholder);
    const editableTitleAccessibleName = hasPlaceholder
        ? placeholder
        : is.nonEmptyStringAndNotWhitespace(title)
          ? title
          : 'Title';
    const titleLayoutClasses = 'min-w-[12rem] flex-1';
    const titleHighlightClasses = clsx(
        'transition-colors duration-200 ease',
        hasHighlight && 'rounded-sm px-xs',
        commentHighlightClassName,
    );

    const { ref: _registerRef, onBlur: registerOnBlur, onChange: registerOnChange, ...registerProps } = register ?? {};

    const syncHiddenInputValue = (value: string): HTMLInputElement | null => {
        const hiddenInputElement = hiddenInputElementRef.current;
        if (is.null(hiddenInputElement)) return null;

        hiddenInputElement.value = value;
        return hiddenInputElement;
    };

    const commitFormValue = (value: string): HTMLInputElement | null => {
        const hiddenInputElement = syncHiddenInputValue(value);
        if (is.null(hiddenInputElement)) return null;

        void registerOnChange?.({
            target: hiddenInputElement,
            type: 'input',
        });
        return hiddenInputElement;
    };

    const handleTitleInput = (event: FormEvent<HTMLSpanElement>): void => {
        const value = getEditableTitleValue(event.currentTarget);
        if (value === '' && event.currentTarget.childNodes.length > 0) {
            event.currentTarget.textContent = '';
        }
        syncHiddenInputValue(value);
        if (!hasNotifiedInputRef.current && value !== title) {
            hasNotifiedInputRef.current = true;
            onInput?.();
        }
    };

    const handleTitleBlur = (event: FocusEvent<HTMLSpanElement>): void => {
        const hiddenInputElement = commitFormValue(getEditableTitleValue(event.currentTarget));
        if (is.null(hiddenInputElement)) return;

        void registerOnBlur?.({ target: hiddenInputElement, type: 'blur' });
    };

    const handleTitleKeyDown = (event: KeyboardEvent<HTMLSpanElement>): void => {
        if (event.key === 'Enter') {
            event.preventDefault();
        }
    };

    const content = isEditing ? (
        <div
            className={titleLayoutClasses}
            data-comment-active={isCommentActive}
            data-has-comment={hasComment}
            data-testid="sidepanel-title-input-wrapper"
        >
            <div className="relative min-w-0">
                <input {...registerProps} ref={hiddenInputRef} type="hidden" />
                {/* biome-ignore lint/a11y/useSemanticElements: the title editor intentionally uses contenteditable so long titles wrap while editing. */}
                <span
                    aria-label={editableTitleAccessibleName}
                    aria-multiline={false}
                    aria-placeholder={hasPlaceholder ? placeholder : undefined}
                    className={clsx(
                        'm-0 min-h-[1lh] w-full cursor-text whitespace-normal outline-none empty:block',
                        'empty:before:pointer-events-none empty:before:text-gray-700 empty:before:content-[attr(data-placeholder)]',
                        TITLE_INPUT_TYPOGRAPHY,
                        titleHighlightClasses,
                    )}
                    contentEditable={true}
                    data-placeholder={hasPlaceholder ? placeholder : undefined}
                    data-testid="sidepanel-title-text"
                    ref={composedInputRef}
                    role="textbox"
                    spellCheck={true}
                    style={TITLE_STYLE}
                    suppressContentEditableWarning={true}
                    tabIndex={0}
                    onBlur={handleTitleBlur}
                    onInput={handleTitleInput}
                    onKeyDown={handleTitleKeyDown}
                />
            </div>
        </div>
    ) : (
        <Title
            className="min-w-48 flex-1"
            data-comment-active={isCommentActive}
            data-has-comment={hasComment}
            data-testid="sidepanel-title"
            data-tile-title=""
        >
            <span className={clsx('inline', titleHighlightClasses)} data-testid="sidepanel-title-text">
                {title}
            </span>
        </Title>
    );

    const isInteractive = !is.undefined(onRowClick) && !isEditing;
    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
        if (!isInteractive) return;
        if (event.target !== event.currentTarget) return;
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onRowClick?.();
        }
    };
    const interactiveAttrs = isInteractive
        ? {
              onClick: onRowClick,
              onKeyDown: handleKeyDown,
              role: 'button',
              tabIndex: 0,
          }
        : {};

    if (is.undefined(stamps)) {
        return (
            <div className={rowClasses} {...interactiveAttrs} {...rowAttrs}>
                {content}
            </div>
        );
    }

    return (
        <div className={rowClasses} {...interactiveAttrs} {...rowAttrs}>
            {content}
            <div
                aria-hidden={isEditing ? true : undefined}
                className={clsx('flex shrink-0 items-center justify-end gap-s', isEditing && 'invisible')}
                data-testid="sidepanel-title-stamps-slot"
            >
                {stamps}
            </div>
        </div>
    );
};
